import React, { useState, useEffect } from "react";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { useParams } from "react-router-dom";
import { createUrl, get, post } from "../../../network/AxiosInterceptor";
import { useToast } from "../../../components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "../../../components/ui/checkbox";

export const AddOrEditUserContainer = () => {
  const { userId } = useParams();
  const { toast } = useToast();
  const [showLoader, setShowLoader] = useState(true);
  const navigate = useNavigate();

  // Define state variables to hold form data
  const [userData, setUserData] = useState({
    first_name: "",
    last_name: "",
    email_address: "",
    phone: "",
    profile_picture: null,
    role: "",
    is_influencer: false,
    influencer_intro: "",
    instaId: "",
  });

  // Effect to fetch user data if userId is present
  useEffect(() => {
    setShowLoader(false);
    if (userId) {
      setShowLoader(true);
      get(createUrl(`/api/users/one/${userId}`), {})
        .then((response) => {
          const data = response.data;
          setShowLoader(false);
          setUserData({
            first_name: data.firstName,
            last_name: data.lastName,
            email_address: data.email,
            phone: data.phone,
            profile_picture: data.profilePic,
            role: data.role,
            is_influencer: data.is_influencer,
            influencer_intro: data.influencerIntro,
            instaId: data.instaId,
          });
        })
        .catch((error) => {
          setShowLoader(false);
          console.error("Error fetching user data:", error);
        });
    }
  }, [userId]); // Trigger effect when userId changes

  // Function to handle form submission
  const handleSubmit = (event) => {
    event.preventDefault();
    saveOrUpdateUser(userData);
  };

  // Function to save or update user data on the server
  const saveOrUpdateUser = async (userData) => {
    // Example implementation: Send userData to server API
    post(createUrl(`/api/users/create`), {
      id: userId,
      firstName: userData.first_name,
      lastName: userData.last_name,
      profilePic: userData.profile_picture,
      phone: userData.phone,
      email: userData.email_address,
      role: userData.role,
      is_influencer: userData.is_influencer,
      influencerIntro: userData.influencerIntro,
      instaId: userData.instaId,
    })
      .then((response) => {
        if (response.data.success) {
          showToastOnSaveSuccess();
          setTimeout(() => navigate(`/admin/users`), 3000);
        } else {
          showToastOnSaveFailed();
        }
      })
      .catch(function (error) {
        console.log(error);
        showToastOnSaveFailed();
      });
  };

  const showToastOnSaveSuccess = () => {
    toast({
      title: "Exploro",
      description: "Saved Successfully",
    });
  };

  const showToastOnSaveFailed = () => {
    toast({
      title: "Exploro",
      description: "User data could not be saved.",
      variant: "destructive",
    });
  };

  // Function to handle changes in form inputs
  const handleInputChange = (event) => {
    const { id, value} = event.target;
    const user = { ...userData, [id]: value };
    setUserData(user);
  };

  const onInfluencerCheckedChange = (isChecked) => {
    const user = { ...userData, is_influencer: isChecked };
    setUserData(user);
  };

  const onRoleChanged = (value) => {
    const user = { ...userData, role: value };
    setUserData(user);
  };

  if (showLoader) {
    return (
      <>
        <div className="flex flex-col items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
          <div
            className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="status"
          ></div>
          <p className="mt-4 text-lg font-semibold text-gray-700 dark:text-gray-300">
            Loading ...
          </p>
        </div>
      </>
    );
  }

  return (
    <main className="w-full max-w-3xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <div className="shadow sm:rounded-md sm:overflow-hidden">
        <div className="px-4 py-5 bg-white sm:p-6">
          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-3">
              <Label htmlFor="first_name">First name</Label>
              <Input
                id="first_name"
                placeholder="First name"
                defaultValue={userData.first_name}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <Label htmlFor="last_name">Last name</Label>
              <Input
                id="last_name"
                placeholder="Last name"
                defaultValue={userData.last_name}
              />
            </div>
            <div className="col-span-6 sm:col-span-4">
              <Label htmlFor="email_address">Email address</Label>
              <Input
                id="email_address"
                placeholder="Email address"
                defaultValue={userData.email_address}
                type="email"
                onChange={handleInputChange}
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <Label htmlFor="phone">phone</Label>
              <Input
                id="phone"
                placeholder="Phone"
                defaultValue={userData.phone}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-span-6">
              <Label htmlFor="profile_picture">Profile Picture</Label>
              <Input
                id="profile_picture"
                placeholder="Profile Pic"
                defaultValue={userData.profile_picture}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <Label htmlFor="role">Role</Label>
              <Select
                id="role"
                onValueChange={onRoleChanged}
                defaultValue={userData.role}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-6 sm:col-span-3">
              <Label htmlFor="is_influencer">Is Influencer?</Label>
              <Checkbox
                className="ms-4"
                id="is_influencer"
                checked={userData.is_influencer}
                onCheckedChange={onInfluencerCheckedChange}
              />
            </div>
            <div className="col-span-6">
              <Label htmlFor="influencer_intro">Influencer Intro</Label>
              <Textarea
                className="min-h-[100px]"
                id="influencer_intro"
                placeholder="Influencer Introduction"
                defaultValue={userData.influencer_intro}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <Label htmlFor="instaId">Insta ID</Label>
              <Input
                id="instaId"
                placeholder="Instagram Id"
                defaultValue={userData.instaId}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
          <Button type="submit" onClick={handleSubmit}>
            Save
          </Button>
        </div>
      </div>
    </main>
  );
};
